import classNames from "classnames";
import camelCase from "lodash/camelCase";
import { requirePropertyOf } from "utils/security";

import { useStyles } from "./styles";

export type TTypographyVariant =
  | "body_med"
  | "body_reg"
  | "body_sb"
  | "h1_lit"
  | "h2_sb"
  | "header_sb"
  | "lg_header_lit"
  | "text_reg"
  | "text_sm_reg"
  | "text_sm_sb"
  | "text_title_sb"
  | "text_tny_reg"
  | "text_tny_sb"
  | "title_med"
  | "title_sb";

type CamelCase<S extends string> = S extends `${infer P1}_${infer P2}${infer P3}`
  ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
  : Lowercase<S>;

const toCamelCaseVariant = <T extends TTypographyVariant>(variant: T): CamelCase<T> =>
  camelCase(variant) as CamelCase<T>;

export type TTypograpyProps = {
  monospace?: boolean;
  multiLine?: boolean;
  noWrap?: boolean;
  relative?: boolean;
  variant?: TTypographyVariant;
};

export const Typography: FC<TTypograpyProps> = ({
  children,
  className,
  monospace = false,
  multiLine = false,
  noWrap = false,
  ref,
  relative = false,
  variant = "body_reg",
  ...restProps
}) => {
  const classes = useStyles();
  const cssVariant = toCamelCaseVariant(variant);
  const variantClassName = requirePropertyOf(classes, cssVariant);

  return (
    <div
      {...restProps}
      className={classNames(
        classes.typography,
        variantClassName,
        {
          [classes.monospace]: monospace,
          [classes.multiLine]: multiLine,
          [classes.noWrap]: noWrap,
          [classes.relative]: relative,
        },
        className,
      )}
      ref={ref}
    >
      {children}
    </div>
  );
};
